import { type ReactNode, createContext, useContext } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

interface BreadcrumbItem {
  label: string | ReactNode;
  href?: string;
  isHome?: boolean;
}

// Contexto para pasar información adicional a los breadcrumbs
interface BreadcrumbContextValue {
  eventTitle?: string;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({});

export function BreadcrumbProvider({
  children,
  eventTitle,
}: {
  children: ReactNode;
  eventTitle?: string;
}) {
  return <BreadcrumbContext.Provider value={{ eventTitle }}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbContext() {
  return useContext(BreadcrumbContext);
}

// Mapeo de rutas a labels legibles
const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  events: 'Mis Eventos',
  new: 'Crear Evento',
  edit: 'Editar',
  map: 'Mapa de Asientos',
  analytics: 'Analíticas',
  attendees: 'Asistentes',
  checkin: 'Check-in',
  payments: 'Pagos',
  users: 'Users',
  admins: 'Administrators',
  reports: 'Reportes',
  'work-orders': 'Work Orders',
  technicians: 'Technicians',
  customers: 'Customers',
  subscriptions: 'Subscriptions',
  devices: 'Devices & Inventory',
};

// Ícono de Home
function HomeIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

export function Breadcrumbs() {
  const location = useLocation();
  const params = useParams();
  const { eventTitle } = useBreadcrumbContext();

  const currentEventId = params.id;

  // No mostrar breadcrumbs en la raíz del dashboard
  if (location.pathname === '/dashboard') {
    return null;
  }

  // Parsear la ruta actual en segmentos
  const pathSegments = location.pathname.split('/').filter((segment) => segment !== '');

  // Generar los items del breadcrumb
  const breadcrumbs: BreadcrumbItem[] = [];

  // Siempre agregar Home primero
  breadcrumbs.push({
    label: <HomeIcon />,
    href: '/dashboard',
    isHome: true,
  });

  let currentPath = '';

  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i];
    currentPath += `/${segment}`;

    // Saltar el primer segmento 'dashboard'
    if (segment === 'dashboard') {
      continue;
    }

    // Si es un ID de evento, mostrar el nombre del evento
    if (currentEventId && segment === currentEventId) {
      const isEventDashboard = i === pathSegments.length - 1;
      breadcrumbs.push({
        label: eventTitle || 'Evento',
        href: isEventDashboard ? undefined : `/dashboard/events/${currentEventId}`,
      });
      continue;
    }

    // Usar el label del mapeo o capitalizar el segmento
    const label = routeLabels[segment] || capitalize(segment);

    // El último segmento no tiene href (es la página actual)
    const isLast = i === pathSegments.length - 1;

    breadcrumbs.push({
      label,
      href: isLast ? undefined : currentPath,
    });
  }

  // Si solo hay el home, no mostrar nada
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex items-center flex-wrap gap-1 text-[13px]">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <span className="mx-1.5 text-gray-400 text-xs">{'>'}</span>
              )}

              {crumb.href && !isLast ? (
                <Link
                  to={crumb.href}
                  className={`transition-colors ${
                    crumb.isHome 
                      ? 'text-gray-400 hover:text-gray-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={isLast ? 'text-gray-900 font-medium' : 'text-gray-500'}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
